import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import type { PropsWithChildren } from 'react'
import { setupBackgroundSync } from '@/lib/sync'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { canUseRecorderOffline, useAuthStateSnapshot } from '@/lib/authStatus'
import { fetchDisclaimerStatus } from '@/lib/api/me'
import {
  clearAllAcknowledged,
  clearAllCachedTexts,
  clearAllRequirementCache,
  getDisclaimerRequirementKey,
  getDisclaimerStorageKey,
  getDisclaimerTextsKey,
  hashDisclaimerTexts,
  persistAcknowledged,
  persistCachedTexts,
  persistDisclaimerRequirement,
  readAcknowledged,
  readCachedDisclaimerRequirement,
  readCachedTexts,
  type DisclaimerRequirement,
} from '@/lib/disclaimerStorage'
import disclaimerContent from '../../../disclaimer.md?raw'

const DEFAULT_DISCLAIMER_TEXT = disclaimerContent.trim()
const DEFAULT_DISCLAIMER_HASH = hashDisclaimerTexts([DEFAULT_DISCLAIMER_TEXT])

// Custom per-binder texts when the server provided any, default text otherwise.
function toShownTexts(customTexts: string[]): string[] {
  return customTexts.length > 0 ? customTexts : [DEFAULT_DISCLAIMER_TEXT]
}

function LoadingScreen({
  message = 'Checking your session…',
}: {
  message?: string
}): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-navy text-white">
      <div className="text-center">
        <p className="text-lg font-semibold">{message}</p>
      </div>
    </div>
  )
}

function OfflineNotice(): JSX.Element {
  return (
    <div className="rounded-xl border border-cyan-500/40 bg-cyan-500/10 p-4 text-sm text-cyan-100">
      <p className="font-semibold">Offline mode</p>
      <p className="text-cyan-50/80">
        You can keep capturing insights, but uploads and taxonomy refreshes will resume once you
        reconnect and sign in.
      </p>
    </div>
  )
}

function LoginScreen({
  disabled,
  onLogin,
  errorMessage,
}: {
  disabled: boolean
  onLogin: () => Promise<void>
  errorMessage?: string
}): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-4 text-white">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-navy-light px-6 py-8 shadow-lg">
        <h1 className="text-2xl font-semibold">VSAI Congress Solution</h1>
        <p className="mt-2 text-sm text-white/80">
          Sign in with your VSAI credentials.
        </p>
        {errorMessage ? (
          <p className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-100">
            {errorMessage}
          </p>
        ) : null}
        <button
          type="button"
          className="mt-6 w-full rounded-xl bg-cyan-500 px-4 py-3 text-sm font-semibold text-navy transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:bg-cyan-500/40 disabled:text-navy/40"
          onClick={onLogin}
          disabled={disabled}
        >
          {disabled ? 'Connect to the internet to sign in' : 'Continue to sign in'}
        </button>
      </div>
    </div>
  )
}

function DisclaimerScreen({
  texts,
  onAgree,
}: {
  texts: string[]
  onAgree: () => void
}): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-4 text-white">
      <div className="w-full max-w-2xl space-y-6 rounded-2xl border border-white/10 bg-navy-light px-6 py-8 shadow-lg">
        <div>
          <h1 className="text-2xl font-semibold">Please review and agree</h1>
          <p className="mt-2 text-sm text-white/80">
            Continue after acknowledging the disclaimer below.
          </p>
        </div>
        <div className="space-y-3">
          {texts.map((text) => (
            <div
              key={text}
              className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/90"
            >
              <p className="whitespace-pre-line">{text}</p>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="w-full rounded-xl bg-cyan-500 px-4 py-3 text-sm font-semibold text-navy transition hover:bg-cyan-400"
          onClick={onAgree}
        >
          Agree
        </button>
      </div>
    </div>
  )
}

export function LoginGate({ children }: PropsWithChildren): JSX.Element {
  const { isAuthenticated, isLoading, loginWithRedirect, error } = useAuth0()
  const online = useOnlineStatus()
  const { userSub } = useAuthStateSnapshot()
  const redirectInFlightRef = useRef(false)
  const storageKey = useMemo(() => getDisclaimerStorageKey(userSub), [userSub])
  const requirementKey = useMemo(
    () => getDisclaimerRequirementKey(userSub),
    [userSub],
  )
  const textsCacheKey = useMemo(() => getDisclaimerTextsKey(userSub), [userSub])
  const [customTexts, setCustomTexts] = useState<string[]>(() =>
    readCachedTexts(textsCacheKey),
  )
  const [hasAcknowledgedDisclaimer, setHasAcknowledgedDisclaimer] = useState(
    // Re-derive the cached texts rather than reading the sibling state — both
    // initializers must agree on the hash without ordering assumptions.
    () =>
      readAcknowledged(
        storageKey,
        userSub,
        hashDisclaimerTexts(toShownTexts(readCachedTexts(textsCacheKey))),
        DEFAULT_DISCLAIMER_HASH,
      ),
  )
  const [disclaimerRequirement, setDisclaimerRequirement] =
    useState<DisclaimerRequirement>(() =>
      readCachedDisclaimerRequirement(requirementKey),
    )
  // Tracks whether we have any answer (cached or fresh from the server) for
  // the current user. We hold the LoadingScreen until this flips true so the
  // disclaimer doesn't flash for users whose binders opt them out.
  const [hasResolvedRequirement, setHasResolvedRequirement] = useState(
    () => readCachedDisclaimerRequirement(requirementKey) !== 'unknown',
  )
  const wasAuthenticatedRef = useRef(isAuthenticated)

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }
    const teardown = setupBackgroundSync()
    return () => teardown()
  }, [isAuthenticated])

  useEffect(() => {
    if (isLoading) {
      return
    }
    if (!isAuthenticated) {
      if (wasAuthenticatedRef.current) {
        clearAllAcknowledged()
        clearAllRequirementCache()
        clearAllCachedTexts()
      }
      wasAuthenticatedRef.current = false
      setHasAcknowledgedDisclaimer(false)
      setDisclaimerRequirement('unknown')
      setHasResolvedRequirement(false)
      setCustomTexts([])
      return
    }
    wasAuthenticatedRef.current = true
    const cachedTexts = readCachedTexts(textsCacheKey)
    setCustomTexts(cachedTexts)
    setHasAcknowledgedDisclaimer(
      readAcknowledged(
        storageKey,
        userSub,
        hashDisclaimerTexts(toShownTexts(cachedTexts)),
        DEFAULT_DISCLAIMER_HASH,
      ),
    )
    const cached = readCachedDisclaimerRequirement(requirementKey)
    setDisclaimerRequirement(cached)
    setHasResolvedRequirement(cached !== 'unknown')
  }, [
    isAuthenticated,
    isLoading,
    requirementKey,
    storageKey,
    textsCacheKey,
    userSub,
  ])

  // Refresh the per-user disclaimer requirement from the server whenever the
  // user becomes authenticated. Offline / fetch failures fall back to the
  // cached value (and ultimately to "show disclaimer" if no cache exists).
  //
  // `userSub` lags `isAuthenticated` by one render — Auth0 reports auth before
  // AuthController flushes the user identity into our snapshot store. Gating
  // on `userSub` prevents firing once with the anon key and again once the
  // real sub arrives.
  useEffect(() => {
    if (isLoading || !isAuthenticated || !userSub) {
      return
    }
    let cancelled = false
    void fetchDisclaimerStatus()
      .then(({ required, texts }) => {
        if (cancelled) {
          return
        }
        persistDisclaimerRequirement(requirementKey, required)
        persistCachedTexts(textsCacheKey, texts)
        setCustomTexts(texts)
        setDisclaimerRequirement(required ? 'required' : 'not-required')
        setHasResolvedRequirement(true)
        if (!required) {
          // Drop any stale ack so we don't carry a meaningless timestamp
          // for users that no longer need the disclaimer.
          try {
            window.localStorage.removeItem(storageKey)
          } catch {
            // ignore storage failures
          }
          setHasAcknowledgedDisclaimer(false)
          return
        }
        // Re-check the ack against the content we are about to show — a
        // changed disclaimer text invalidates a prior ack immediately.
        setHasAcknowledgedDisclaimer(
          readAcknowledged(
            storageKey,
            userSub,
            hashDisclaimerTexts(toShownTexts(texts)),
            DEFAULT_DISCLAIMER_HASH,
          ),
        )
      })
      .catch(() => {
        if (cancelled) {
          return
        }
        // Fail closed: stop waiting so we render the disclaimer (the
        // existing requirement state will fall through to the show branch).
        setHasResolvedRequirement(true)
      })
    return () => {
      cancelled = true
    }
  }, [
    isAuthenticated,
    isLoading,
    requirementKey,
    storageKey,
    textsCacheKey,
    userSub,
  ])

  const redirectToLogin = useCallback(async (): Promise<void> => {
    if (redirectInFlightRef.current) {
      return
    }
    redirectInFlightRef.current = true
    try {
      const runtimeRedirectUri = `${window.location.origin}/auth/callback`
      await loginWithRedirect({
        appState: { returnTo: window.location.pathname },
        authorizationParams: {
          redirect_uri: runtimeRedirectUri,
        },
      })
    } catch (redirectError) {
      console.warn('Auth redirect failed', redirectError)
      redirectInFlightRef.current = false
    }
  }, [loginWithRedirect])

  // When the user is signed out and online, send them straight to the Auth0
  // hosted login instead of showing an intermediate "Continue to sign in" card.
  // Skip when an auth error is present to avoid looping on a failing login.
  useEffect(() => {
    if (isLoading || isAuthenticated || !online || error) {
      return
    }
    if (canUseRecorderOffline()) {
      return
    }
    void redirectToLogin()
  }, [isAuthenticated, isLoading, online, error, redirectToLogin])

  if (isLoading) {
    return <LoadingScreen />
  }

  const offlineRecorderAllowed = canUseRecorderOffline()
  const shouldBlockAccess = !isAuthenticated && !offlineRecorderAllowed
  if (shouldBlockAccess) {
    // Offline: we can't redirect to Auth0, so explain how to unlock access.
    if (!online) {
      return (
        <LoginScreen
          disabled
          onLogin={redirectToLogin}
          errorMessage="Connect to the internet and sign in at least once to unlock offline mode."
        />
      )
    }
    // Auth error: don't auto-redirect (loop risk) — let the user retry manually.
    if (error) {
      return (
        <LoginScreen
          disabled={false}
          onLogin={async () => {
            redirectInFlightRef.current = false
            await redirectToLogin()
          }}
          errorMessage={error.message}
        />
      )
    }
    // Online, no error: the effect above is redirecting to the Auth0 login.
    return <LoadingScreen message="Redirecting to sign in…" />
  }

  // Hold the loading screen until we know whether this user needs the
  // disclaimer. Without this, users whose binders opt them out briefly see
  // the disclaimer between auth completing and /me/disclaimer resolving.
  // Skip if the user already has a valid ack (no reason to gate them); if
  // the fetch fails or times out, the catch handler flips
  // `hasResolvedRequirement` so we fall through to the fail-closed disclaimer.
  if (
    isAuthenticated &&
    !hasResolvedRequirement &&
    !hasAcknowledgedDisclaimer
  ) {
    return <LoadingScreen />
  }

  const skipDisclaimer =
    isAuthenticated && disclaimerRequirement === 'not-required'
  if (isAuthenticated && !skipDisclaimer && !hasAcknowledgedDisclaimer) {
    const shownTexts = toShownTexts(customTexts)
    return (
      <DisclaimerScreen
        texts={shownTexts}
        onAgree={() => {
          persistAcknowledged(storageKey, hashDisclaimerTexts(shownTexts))
          setHasAcknowledgedDisclaimer(true)
        }}
      />
    )
  }

  return (
    <>
      {!isAuthenticated && offlineRecorderAllowed ? <OfflineNotice /> : null}
      {children}
    </>
  )
}
